// =====================================================
// utils/resultados.ts
// Funciones compartidas para cálculo de resultados
// =====================================================

export type Deporte = "Tenis" | "Padel" | "Futbol";

/**
 * Calcula el ganador de un partido según el deporte
 */
export function calcularGanador(
  sets: Array<{ gamesLocal: number; gamesVisitante: number }>,
  equipoLocal: any,
  equipoVisitante: any,
  deporte: Deporte
): { ganador: any | null; resultado: string } {
  if (deporte === "Futbol") {
    // En fútbol solo hay un "set" con los goles
    const golesLocal = sets[0].gamesLocal;
    const golesVisitante = sets[0].gamesVisitante;

    let ganador = null;
    if (golesLocal > golesVisitante) {
      ganador = equipoLocal;
    } else if (golesVisitante > golesLocal) {
      ganador = equipoVisitante;
    }
    // Si es empate, ganador queda null

    const resultado = `${golesLocal}-${golesVisitante}`;
    
    return { ganador, resultado };
  } else {
    // Tenis/Padel: contar sets ganados
    let setsLocal = 0;
    let setsVisitante = 0;

    sets.forEach((s) => {
      if (s.gamesLocal > s.gamesVisitante) setsLocal++;
      else if (s.gamesVisitante > s.gamesLocal) setsVisitante++;
    });

    let ganador = null;
    if (setsLocal > setsVisitante) {
      ganador = equipoLocal;
    } else if (setsVisitante > setsLocal) {
      ganador = equipoVisitante;
    }

    const resultado = sets
      .map((s) => `${s.gamesLocal}-${s.gamesVisitante}`)
      .join(" ");

    return { ganador, resultado };
  }
}

/**
 * Calcula estadísticas de un equipo en una lista de partidos
 */
export function calcularEstadisticas(
  partidos: any[],
  equipoId: number,
  deporte: Deporte
): {
  pj: number;
  pg: number;
  pe: number; // Empates (solo fútbol)
  pp: number;
  puntos: number;
  setsGanados: number;
  setsPerdidos: number;
  gamesGanados: number;
  gamesPerdidos: number;
} {
  let pj = 0;
  let pg = 0;
  let pe = 0;
  let pp = 0;
  let puntos = 0;
  let setsGanados = 0;
  let setsPerdidos = 0;
  let gamesGanados = 0;
  let gamesPerdidos = 0;

  partidos.forEach((partido) => {
    if (!partido.sets || partido.sets.length === 0) return;

    const esLocal = partido.local.id === equipoId;
    const esVisitante = partido.visitante.id === equipoId;

    if (!esLocal && !esVisitante) return;

    pj++;

    if (deporte === "Futbol") {
      // Fútbol: solo 1 "set" con goles
      const golesLocal = partido.sets[0]?.gamesLocal || 0;
      const golesVisitante = partido.sets[0]?.gamesVisitante || 0;

      if (esLocal) {
        gamesGanados += golesLocal;
        gamesPerdidos += golesVisitante;

        if (golesLocal > golesVisitante) {
          pg++;
          puntos += 3;
        } else if (golesLocal === golesVisitante) {
          pe++;
          puntos += 1;
        } else {
          pp++;
        }
      } else {
        gamesGanados += golesVisitante;
        gamesPerdidos += golesLocal;

        if (golesVisitante > golesLocal) {
          pg++;
          puntos += 3;
        } else if (golesVisitante === golesLocal) {
          pe++;
          puntos += 1;
        } else {
          pp++;
        }
      }

      // En fútbol, "sets" no aplica (dejar en 0)
      setsGanados = 0;
      setsPerdidos = 0;
    } else {
      // Tenis/Padel: múltiples sets
      let misSets = 0;
      let susSets = 0;

      partido.sets.forEach((set: any) => {
        if (esLocal) {
          gamesGanados += set.gamesLocal || 0;
          gamesPerdidos += set.gamesVisitante || 0;

          if (set.gamesLocal > set.gamesVisitante) misSets++;
          else if (set.gamesVisitante > set.gamesLocal) susSets++;
        } else {
          gamesGanados += set.gamesVisitante || 0;
          gamesPerdidos += set.gamesLocal || 0;

          if (set.gamesVisitante > set.gamesLocal) misSets++;
          else if (set.gamesLocal > set.gamesVisitante) susSets++;
        }
      });

      setsGanados += misSets;
      setsPerdidos += susSets;

      if (misSets > susSets) {
        pg++;
        puntos += 3;
      } else if (susSets > misSets) {
        pp++;
      }
      // En tenis no hay empates
    }
  });

  return {
    pj,
    pg,
    pe,
    pp,
    puntos,
    setsGanados,
    setsPerdidos,
    gamesGanados,
    gamesPerdidos,
  };
}